/*
 * store-hours.js
 * --------------------------------------------------------------------------
 * Single source of truth for "Open now / Closed" status and the human-readable
 * hours list, driven by the distributor `hours_json` metafield.
 *
 * hours_json shape (times are 24h "HH:MM" in the STORE's local time):
 *   {
 *     "days": {
 *       "Mon": [["07:00","16:00"]],
 *       "Tue": [["07:00","16:00"]],
 *       "Wed": [["07:00","16:00"]],
 *       "Thu": [["07:00","16:00"]],
 *       "Fri": [["07:00","16:00"]],
 *       "Sat": [],            // empty array = closed that day
 *       "Sun": []
 *     },
 *     "tz": "America/New_York" // OPTIONAL override; normally resolved from state
 *   }
 *
 *   - Multiple intervals per day are allowed (lunch break):
 *       "Mon": [["09:00","13:00"],["14:00","18:00"]]
 *   - Overnight intervals work automatically (close < open => spans midnight).
 *
 * Timezone is resolved from the store's STATE (built-in STATE_TZ table below),
 * unless hours_json carries an explicit "tz". Current local time at the store
 * is computed with the built-in Intl API, so the status is correct regardless
 * of where the visitor sits in the world.
 *
 * Exposes: window.StoreHours.render(hoursJson, state, opts)
 *   -> { statusHtml, listHtml }  when there is usable data
 *   -> null                      when there is no data (caller shows nothing)
 * --------------------------------------------------------------------------
 */
(function (global) {
  'use strict';

  if (global.StoreHours) return; // already loaded (multiple sections may include it)

  // index === Date.getDay():  0=Sun ... 6=Sat
  var DAY_BY_INDEX = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // display order, Mon-first
  var DISPLAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  var WEEKDAY_TO_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  // Dominant IANA timezone per US state/territory.
  // Multi-zone states resolve to their most-populous zone; use hours_json.tz
  // to override a specific store (e.g. a Florida panhandle location -> Central).
  var STATE_TZ = {
    AL: 'America/Chicago',    AK: 'America/Anchorage',  AZ: 'America/Phoenix',
    AR: 'America/Chicago',    CA: 'America/Los_Angeles',CO: 'America/Denver',
    CT: 'America/New_York',   DE: 'America/New_York',   DC: 'America/New_York',
    FL: 'America/New_York',   GA: 'America/New_York',   HI: 'Pacific/Honolulu',
    ID: 'America/Denver',     IL: 'America/Chicago',    IN: 'America/New_York',
    IA: 'America/Chicago',    KS: 'America/Chicago',    KY: 'America/New_York',
    LA: 'America/Chicago',    ME: 'America/New_York',   MD: 'America/New_York',
    MA: 'America/New_York',   MI: 'America/New_York',   MN: 'America/Chicago',
    MS: 'America/Chicago',    MO: 'America/Chicago',    MT: 'America/Denver',
    NE: 'America/Chicago',    NV: 'America/Los_Angeles',NH: 'America/New_York',
    NJ: 'America/New_York',   NM: 'America/Denver',     NY: 'America/New_York',
    NC: 'America/New_York',   ND: 'America/Chicago',    OH: 'America/New_York',
    OK: 'America/Chicago',    OR: 'America/Los_Angeles',PA: 'America/New_York',
    RI: 'America/New_York',   SC: 'America/New_York',   SD: 'America/Chicago',
    TN: 'America/Chicago',    TX: 'America/Chicago',    UT: 'America/Denver',
    VT: 'America/New_York',   VA: 'America/New_York',   WA: 'America/Los_Angeles',
    WV: 'America/New_York',   WI: 'America/Chicago',    WY: 'America/Denver',
    PR: 'America/Puerto_Rico'
  };

  // Full state names -> 2-letter code, so either form in the data works.
  var STATE_NAME_TO_CODE = {
    ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR', CALIFORNIA: 'CA',
    COLORADO: 'CO', CONNECTICUT: 'CT', DELAWARE: 'DE', FLORIDA: 'FL', GEORGIA: 'GA',
    HAWAII: 'HI', IDAHO: 'ID', ILLINOIS: 'IL', INDIANA: 'IN', IOWA: 'IA',
    KANSAS: 'KS', KENTUCKY: 'KY', LOUISIANA: 'LA', MAINE: 'ME', MARYLAND: 'MD',
    MASSACHUSETTS: 'MA', MICHIGAN: 'MI', MINNESOTA: 'MN', MISSISSIPPI: 'MS',
    MISSOURI: 'MO', MONTANA: 'MT', NEBRASKA: 'NE', NEVADA: 'NV',
    'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ', 'NEW MEXICO': 'NM', 'NEW YORK': 'NY',
    'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', OHIO: 'OH', OKLAHOMA: 'OK',
    OREGON: 'OR', PENNSYLVANIA: 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD', TENNESSEE: 'TN', TEXAS: 'TX', UTAH: 'UT', VERMONT: 'VT',
    VIRGINIA: 'VA', WASHINGTON: 'WA', 'WEST VIRGINIA': 'WV', WISCONSIN: 'WI',
    WYOMING: 'WY', 'WASHINGTON DC': 'DC', 'DISTRICT OF COLUMBIA': 'DC',
    'PUERTO RICO': 'PR'
  };

  function resolveTimezone(hours, state) {
    if (hours && typeof hours.tz === 'string' && hours.tz) return hours.tz;
    if (!state) return null;
    var key = String(state).trim().toUpperCase();
    if (STATE_TZ[key]) return STATE_TZ[key];
    if (STATE_NAME_TO_CODE[key]) return STATE_TZ[STATE_NAME_TO_CODE[key]];
    return null;
  }

  // "HH:MM" -> minutes since midnight. "24:00" allowed as an end-of-day close.
  function toMinutes(str) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(str).trim());
    if (!m) return null;
    var h = parseInt(m[1], 10);
    var min = parseInt(m[2], 10);
    if (h === 24 && min === 0) return 1440;
    if (h > 23 || min > 59) return null;
    return h * 60 + min;
  }

  // minutes -> "7:00 AM"
  function formatTime(min) {
    if (min >= 1440) return '12:00 AM';
    var h = Math.floor(min / 60);
    var m = min % 60;
    var ampm = h < 12 ? 'AM' : 'PM';
    var h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return h12 + ':' + (m < 10 ? '0' + m : m) + ' ' + ampm;
  }

  function getDays(hours) {
    if (!hours) return null;
    var days = hours.days || hours; // tolerate {days:{...}} or a bare {...}
    if (typeof days !== 'object') return null;
    var hasAny = false;
    for (var i = 0; i < DISPLAY_ORDER.length; i++) {
      if (Object.prototype.hasOwnProperty.call(days, DISPLAY_ORDER[i])) { hasAny = true; break; }
    }
    return hasAny ? days : null;
  }

  function intervalsFor(days, dayName) {
    var arr = days[dayName];
    return Array.isArray(arr) ? arr : [];
  }

  // Parse one ["07:00","16:00"] into {open, close, overnight}. null if invalid.
  function parseInterval(iv) {
    if (!Array.isArray(iv) || iv.length < 2) return null;
    var open = toMinutes(iv[0]);
    var close = toMinutes(iv[1]);
    if (open === null || close === null) return null;
    return { open: open, close: close, overnight: close <= open };
  }

  // Current {dayIndex, minutes} at the given IANA timezone, via Intl. null on failure.
  function nowAtTimezone(tz) {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(new Date());
      var map = {};
      for (var i = 0; i < parts.length; i++) map[parts[i].type] = parts[i].value;
      var idx = WEEKDAY_TO_INDEX[map.weekday];
      if (idx === undefined) return null;
      var h = parseInt(map.hour, 10);
      if (h === 24) h = 0; // some engines emit "24" at midnight
      return { dayIndex: idx, minutes: h * 60 + parseInt(map.minute, 10) };
    } catch (e) {
      return null;
    }
  }

  // true if the store is open at `now` (handles overnight spillover from prev day).
  function isOpenAt(days, now) {
    var today = intervalsFor(days, DAY_BY_INDEX[now.dayIndex]);
    var i, iv;
    for (i = 0; i < today.length; i++) {
      iv = parseInterval(today[i]);
      if (!iv) continue;
      if (iv.overnight) {
        if (now.minutes >= iv.open) return true; // runs past midnight into tomorrow
      } else if (now.minutes >= iv.open && now.minutes < iv.close) {
        return true;
      }
    }
    // yesterday's overnight interval may still be running this morning
    var yIndex = (now.dayIndex + 6) % 7;
    var yest = intervalsFor(days, DAY_BY_INDEX[yIndex]);
    for (i = 0; i < yest.length; i++) {
      iv = parseInterval(yest[i]);
      if (iv && iv.overnight && now.minutes < iv.close) return true;
    }
    return false;
  }

  // A day's display text, e.g. "7:00 AM – 4:00 PM" or the closed label.
  function dayText(intervals, closedLabel) {
    if (!intervals.length) return closedLabel;
    var parts = [];
    for (var i = 0; i < intervals.length; i++) {
      var iv = parseInterval(intervals[i]);
      if (!iv) continue;
      parts.push(formatTime(iv.open) + ' – ' + formatTime(iv.close));
    }
    return parts.length ? parts.join(', ') : closedLabel;
  }

  // Build grouped lines, merging consecutive days with identical hours.
  // -> "Mon–Fri: 7:00 AM – 4:00 PM\nSat–Sun: Closed"
  function renderList(days, opts) {
    opts = opts || {};
    var closedLabel = opts.closedLabel || 'Closed';
    var rows = DISPLAY_ORDER.map(function (name) {
      return { name: name, text: dayText(intervalsFor(days, name), closedLabel) };
    });
    var groups = [];
    rows.forEach(function (row) {
      var last = groups[groups.length - 1];
      if (last && last.text === row.text) {
        last.end = row.name;
      } else {
        groups.push({ start: row.name, end: row.name, text: row.text });
      }
    });
    return groups.map(function (g) {
      var label = g.start === g.end ? g.start : g.start + '–' + g.end;
      return label + ': ' + g.text;
    }).join('\n');
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Main entry point. Returns {statusHtml, listHtml} or null when no usable data.
  function render(hoursJson, state, opts) {
    opts = opts || {};
    var hours = hoursJson;
    if (typeof hours === 'string') {
      if (!hours.trim()) return null;
      try { hours = JSON.parse(hours); } catch (e) { return null; }
    }
    var days = getDays(hours);
    if (!days) return null;

    // By default newlines are kept as-is (callers using `white-space: pre-line`,
    // e.g. the locator). Pass opts.lineBreak = '<br>' for plain containers.
    var listHtml = escapeHtml(renderList(days, opts));
    if (opts.lineBreak && opts.lineBreak !== '\n') {
      listHtml = listHtml.replace(/\n/g, opts.lineBreak);
    }

    var statusHtml = '';
    var tz = resolveTimezone(hours, state);
    if (tz) {
      var now = nowAtTimezone(tz);
      if (now) {
        var open = isOpenAt(days, now);
        var openLabel = opts.openLabel || 'Open now';
        var closedStatusLabel = opts.closedStatusLabel || 'Closed';
        var statusClass = opts.statusClass || 'store-locator__hours-status';
        statusHtml =
          '<div class="' + statusClass + ' ' + (open ? 'is-open' : 'is-closed') + '">' +
          escapeHtml(open ? openLabel : closedStatusLabel) +
          '</div>';
      }
    }
    return { statusHtml: statusHtml, listHtml: listHtml };
  }

  // Progressive enhancement for server-rendered, static hours blocks.
  // Markup contract (the element's existing content is the no-JS fallback):
  //   <div class="..."
  //        data-store-hours='{"days":{...}}'      // the hours_json metafield
  //        data-store-hours-state="FL"            // store state (for timezone)
  //        data-store-hours-status-class="..."    // optional CSS class for the badge
  //        data-store-hours-status-target="#sel"> // optional: where to put the badge
  //     Mon-Fri 7-16 ...   <!-- fallback text, replaced on success -->
  //   </div>
  // If hours_json is missing/invalid the fallback content is left untouched.
  function enhance(root) {
    root = root || document;
    var nodes = root.querySelectorAll('[data-store-hours]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.getAttribute('data-store-hours-done')) continue;
      var res = render(
        el.getAttribute('data-store-hours'),
        el.getAttribute('data-store-hours-state'),
        {
          lineBreak: '<br>',
          statusClass: el.getAttribute('data-store-hours-status-class') || 'store-hours__status'
        }
      );
      if (!res) continue; // no usable data -> keep the fallback markup
      el.setAttribute('data-store-hours-done', '1');
      // Wrap the list in a block so it sits on its own line below the badge.
      var listBlock = '<div class="store-hours__list">' + res.listHtml + '</div>';
      var targetSel = el.getAttribute('data-store-hours-status-target');
      if (res.statusHtml && targetSel) {
        var target = root.querySelector(targetSel);
        el.innerHTML = listBlock;
        (target || el).insertAdjacentHTML('beforeend', res.statusHtml);
      } else {
        el.innerHTML = (res.statusHtml || '') + listBlock;
      }
    }
  }

  global.StoreHours = {
    render: render,
    renderList: renderList,
    resolveTimezone: resolveTimezone,
    isOpenAt: isOpenAt,
    nowAtTimezone: nowAtTimezone,
    enhance: enhance
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { enhance(document); });
  } else {
    enhance(document);
  }
})(window);
